import { useMemo } from "react";
import { Phone, Server, ShieldAlert, Square, Video } from "lucide-react";

import type { SuggestedHost, SuggestedHostCategory } from "@/api/types";
import { Badge } from "@/components/ui/badge";

const CATEGORY_ICONS: Record<SuggestedHostCategory, React.ElementType> = {
  network: ShieldAlert,
  telephony: Phone,
  video: Video,
  server: Server,
  other: Square,
};

const CATEGORY_LABELS: Record<SuggestedHostCategory, string> = {
  network: "Red",
  telephony: "Telefonía",
  video: "Video",
  server: "Servidores",
  other: "Otros",
};

const CATEGORY_ORDER: SuggestedHostCategory[] = [
  "network",
  "telephony",
  "video",
  "server",
  "other",
];

interface HostPickerProps {
  hosts: SuggestedHost[];
  selectedHostname: string;
  onPickFromCard: (hostname: string) => void;
  onPickFromCombobox: (hostname: string) => void;
  onTypeFree: (value: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  /** True si el endpoint backend respondió pero sin datos (Zabbix off). */
  unavailable?: boolean;
}

export function HostPicker({
  hosts,
  selectedHostname,
  onPickFromCard,
  onPickFromCombobox,
  onTypeFree,
  disabled,
  isLoading,
  unavailable,
}: HostPickerProps) {
  const critical = useMemo(() => hosts.filter((h) => h.is_critical), [hosts]);
  const grouped = useMemo(() => groupByCategory(hosts), [hosts]);
  const datalistId = "host-picker-datalist";

  return (
    <div className="space-y-4">
      {critical.length > 0 && (
        <section aria-labelledby="host-picker-critical-heading">
          <h2
            id="host-picker-critical-heading"
            className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
          >
            Hosts críticos
          </h2>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {critical.map((host) => (
              <CriticalHostCard
                key={host.hostname}
                host={host}
                disabled={disabled}
                onClick={() => onPickFromCard(host.hostname)}
              />
            ))}
          </div>
        </section>
      )}

      {hosts.length > 0 && (
        <section className="space-y-1">
          <label
            htmlFor="host-picker-combobox"
            className="text-sm font-medium"
          >
            Buscar entre todos los hosts del Zabbix
          </label>
          <select
            id="host-picker-combobox"
            className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            value={hosts.some((h) => h.hostname === selectedHostname) ? selectedHostname : ""}
            onChange={(e) => onPickFromCombobox(e.target.value)}
            disabled={disabled}
          >
            <option value="">Selecciona un host…</option>
            {CATEGORY_ORDER.map((cat) => {
              const items = grouped[cat];
              if (!items || items.length === 0) return null;
              return (
                <optgroup key={cat} label={CATEGORY_LABELS[cat]}>
                  {items.map((host) => (
                    <option key={host.hostname} value={host.hostname}>
                      {host.name === host.hostname
                        ? host.hostname
                        : `${host.name} — ${host.hostname}`}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </section>
      )}

      <section className="space-y-1">
        <label htmlFor="host-picker-free" className="text-sm font-medium">
          Host o subsistema
        </label>
        <input
          id="host-picker-free"
          type="text"
          list={datalistId}
          className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          placeholder="u otro nombre, hostname, subsistema"
          value={selectedHostname}
          onChange={(e) => onTypeFree(e.target.value)}
          disabled={disabled}
          autoComplete="off"
          required
        />
        <datalist id={datalistId}>
          {hosts.map((host) => (
            <option key={host.hostname} value={host.hostname}>
              {host.name}
            </option>
          ))}
        </datalist>
        {isLoading && (
          <p className="text-xs text-muted-foreground">Cargando hosts del Zabbix…</p>
        )}
        {!isLoading && unavailable && (
          <p className="text-xs text-muted-foreground">
            Sugerencias no disponibles. Puedes escribir el hostname manualmente.
          </p>
        )}
      </section>
    </div>
  );
}

function CriticalHostCard({
  host,
  onClick,
  disabled,
}: {
  host: SuggestedHost;
  onClick: () => void;
  disabled?: boolean;
}) {
  const Icon = CATEGORY_ICONS[host.category];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="group flex flex-col items-start gap-2 rounded-lg border bg-card p-3 text-left shadow-sm transition-colors hover:border-primary/60 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      aria-label={`Iniciar análisis sobre ${host.name}`}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <Icon
          className="h-5 w-5 text-primary"
          aria-hidden="true"
        />
        <Badge variant="destructive" className="text-[10px] uppercase">
          Crítico
        </Badge>
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold">{host.name}</div>
        <div className="truncate font-mono text-xs text-muted-foreground">
          {host.hostname}
        </div>
      </div>
    </button>
  );
}

function groupByCategory(hosts: SuggestedHost[]): Record<SuggestedHostCategory, SuggestedHost[]> {
  const base: Record<SuggestedHostCategory, SuggestedHost[]> = {
    network: [],
    telephony: [],
    video: [],
    server: [],
    other: [],
  };
  for (const h of hosts) {
    base[h.category].push(h);
  }
  return base;
}
